import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Row, Col, Loader } from '@lucid/lego';
import { useStore } from '../../../store/store';
import Table from './Table/Table';
import { useGetCustomerCases } from '../../../api/services/lupp/customer/customer';
import { useGetLabels } from '../../../hooks/useLabels';
import { convertToContentfulLocale } from '../../../utils/tools';
import styles from './CaseTracker.module.scss';

const CaseTracker: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const locale = useStore((state) => state.locale);
  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useGetLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR',
    locale: convertToContentfulLocale(locale),
  });

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      // Reset to first page when search changes
      if (searchQuery !== debouncedSearch) {
        setCurrentPage(0);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearch]);

  const {
    data: caseData,
    isLoading,
    isFetching,
    error,
  } = useGetCustomerCases({
    page: currentPage,
    size: pageSize,
    searchString: debouncedSearch || undefined,
    enabled: !labelsLoading && !labelsError,
  });

  useEffect(() => {
    // Scroll to top when page changes
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const renderContent = () => {
    if (labelsLoading || isLoading) {
      return (
        <Box pt={24} className={styles.loaderContainer}>
          <Loader size={48} />
        </Box>
      );
    }

    if (labelsError) {
      return (
        <Box pt={24} className={styles.emptyStateMessage}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_labels}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box pt={24} className={styles.emptyStateMessage}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            Error loading cases. Please try again.
          </Typography>
        </Box>
      );
    }

    return (
      <Box pt={24} className={styles.relativePosition}>
        {isFetching && (
          <Box className={styles.loadingOverlay}>
            <Loader size={48} />
          </Box>
        )}
        <Table
          theme={theme}
          data={caseData?.items || []}
          totalCount={caseData?.totalCount || 0}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
        />
      </Box>
    );
  };

  return (
    <Box>
      <Row className={styles.emptyStateMessage}>
        <Col xs={12} md={12} xxl={12}>
          <Typography variant="headline" size="small" color="primary-1" theme={theme}>
            Case Tracker
          </Typography>
          <Box mt={8}>
            <Typography variant="body" size="medium" color="gray-3" theme={theme}>
              View the status of all customer care cases.
            </Typography>
          </Box>
        </Col>
      </Row>
      <Box pt={24}>{renderContent()}</Box>
    </Box>
  );
};

export default CaseTracker;
